import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SecurityFooter from '@/components/feature/SecurityFooter';
import ClientHeader from '@/components/feature/ClientHeader';
import WithdrawalForm from './components/WithdrawalForm';
import { getSessionClient, type ClientAccount } from '@/hooks/useClientAuth';
import { getBankSession, type BankSession } from '@/hooks/useBankAuth';
import { saveDraft, type WithdrawalDraft } from '@/hooks/useWithdrawalFlow';

export default function WithdrawalPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientAccount | null>(null);
  const [bank, setBank] = useState<BankSession | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const session = await getSessionClient();
      if (!session) {
        navigate('/');
        return;
      }
      if (active) {
        setClient(session);
        setBank(getBankSession());
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  // Le formulaire de retrait est désormais la première étape : une fois
  // renseigné, le client est dirigé vers l'authentification bancaire (ou
  // directement vers le récapitulatif si sa banque est déjà authentifiée).
  const handleSubmit = (draft: WithdrawalDraft) => {
    saveDraft(draft);
    const currentBank = getBankSession();
    navigate(currentBank?.verified ? '/withdrawal/recap' : '/withdrawal/bank');
  };

  if (!client) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-lg"></i>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <ClientHeader
        clientName={client.fullName}
        backTo="/dashboard"
        backLabel={t('withdrawal.backDashboard')}
      />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-2xl px-4 py-8 md:px-6 md:py-10">
          <div className="animate-fade-rise">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-900">
              <i className="ri-bank-card-2-line text-sm"></i>
              {t('brand.name')}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t('withdrawal.title')}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              {t('withdrawal.subtitle')}
            </p>

            {bank && (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5">
                <span className="flex items-center gap-2 text-xs font-semibold text-primary-900">
                  <i className="ri-shield-check-line text-sm"></i>
                  {t('withdrawal.bankVerified')}
                </span>
                <span className="hidden h-4 w-px bg-primary-200 sm:block"></span>
                <span className="text-xs text-foreground-600">{bank.bankName}</span>
                <a
                  href="/withdrawal/bank"
                  className="ml-auto cursor-pointer whitespace-nowrap text-xs font-semibold text-primary-700 underline-offset-2 hover:underline"
                >
                  {t('withdrawal.changeBank')}
                </a>
              </div>
            )}
          </div>

          <div className="mt-8 animate-fade-rise">
            <WithdrawalForm client={client} onSubmit={handleSubmit} />
          </div>
        </div>
      </main>

      <SecurityFooter />
    </div>
  );
}